import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

// ── Helpers ────────────────────────────────────────────────────

function buildSchemaMarkup(faqs, businessName) {
  return `<script type="application/ld+json">
${JSON.stringify({
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": `${businessName} FAQs`,
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer }
    }))
  }, null, 2)}
</script>`;
}

function buildHTMLBlock(faqs) {
  const items = faqs.map(f =>
    `  <div class="faq-item">\n    <h3 class="faq-question">${f.question}</h3>\n    <div class="faq-answer"><p>${f.answer}</p></div>\n  </div>`
  ).join('\n\n');
  return `<section class="faq-section">\n  <h2>Frequently Asked Questions</h2>\n\n${items}\n\n</section>\n\n${buildSchemaMarkup(faqs)}`;
}

// ── Step 1: Input ──────────────────────────────────────────────

function StepInput({ clients, onNext }) {
  const [form, setForm] = useState({ keyword: '', service: '', location: '', clientId: '', businessName: '', description: '', locationCode: 2826 });
  const [error, setError] = useState('');
  const up = (f, v) => setForm(p => ({ ...p, [f]: v }));

  const handleClientChange = (id) => {
    const c = clients.find(c => c.id === id);
    up('clientId', id);
    if (c) { up('businessName', c.name); up('description', c.description || ''); up('locationCode', c.location_code || 2826); }
  };

  const handleNext = () => {
    if (!form.keyword.trim()) { setError('Please enter a keyword or service.'); return; }
    if (!form.businessName.trim()) { setError('Please enter a business name.'); return; }
    onNext(form);
  };

  return (
    <div className="card">
      <div className="card-title">Service & client details</div>
      <div className="card-sub">Claude will generate realistic FAQ questions, then write SEO-optimised answers for each one you select.</div>

      <div className="form-group">
        <label>Link to client (optional)</label>
        <select value={form.clientId} onChange={e => handleClientChange(e.target.value)}>
          <option value="">— No client selected —</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </div>

      <div className="form-row form-row-2 form-group">
        <div>
          <label>Main keyword / service *</label>
          <input type="text" placeholder="e.g. carpet cleaning" value={form.keyword} onChange={e => up('keyword', e.target.value)} autoFocus />
        </div>
        <div>
          <label>Location (optional)</label>
          <input type="text" placeholder="e.g. Bromley, Kent" value={form.location} onChange={e => up('location', e.target.value)} />
        </div>
      </div>

      <div className="form-row form-row-2 form-group">
        <div>
          <label>Business name *</label>
          <input type="text" placeholder="e.g. Carpet Bright UK" value={form.businessName} onChange={e => up('businessName', e.target.value)} />
        </div>
        <div>
          <label>Service type</label>
          <input type="text" placeholder="e.g. Carpet cleaning company" value={form.service} onChange={e => up('service', e.target.value)} />
        </div>
      </div>

      <div className="form-group" style={{ marginBottom: 0 }}>
        <label>Business description (optional)</label>
        <textarea rows={2} placeholder="Brief description — helps make questions and answers more specific" value={form.description} onChange={e => up('description', e.target.value)} />
      </div>

      {error && <div className="alert alert-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="actions">
        <button className="btn btn-primary" onClick={handleNext}>Generate questions →</button>
      </div>
    </div>
  );
}

// ── Step 2: Question selection ─────────────────────────────────

function StepQuestions({ inputData, savedClient, onBack, onNext }) {
  const [questions, setQuestions] = useState([]);
  const [selected, setSelected] = useState([]);
  const [custom, setCustom] = useState('');
  const [loading, setLoading] = useState(true);
  const [answerLength, setAnswerLength] = useState('medium');
  const [eatInfo, setEatInfo] = useState({
    phone: savedClient?.phone || '',
    years_trading: savedClient?.years_trading || '',
    accreditations: savedClient?.accreditations || '',
    stats: savedClient?.stats || '',
    unique_selling_points: savedClient?.unique_selling_points || '',
  });
  const [error, setError] = useState('');
  const upEat = (f, v) => setEatInfo(p => ({ ...p, [f]: v }));

  useEffect(() => { loadQuestions(); }, []);

  const loadQuestions = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/content/faq-questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          keyword: inputData.keyword,
          service: inputData.service,
          location: inputData.location,
          businessName: inputData.businessName,
          businessDescription: inputData.description,
        })
      });
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      const qs = data.questions || [];
      setQuestions(qs);
      setSelected(qs.slice(0, Math.min(10, qs.length)));
    } catch (e) {
      setError('Could not generate questions — please try again.');
      console.error(e);
    }
    setLoading(false);
  };

  const toggle = (q) => setSelected(prev => prev.includes(q) ? prev.filter(x => x !== q) : [...prev, q]);

  const addCustom = () => {
    const v = custom.trim();
    if (v && !questions.includes(v)) {
      setQuestions(prev => [...prev, v]);
      setSelected(prev => [...prev, v]);
      setCustom('');
    }
  };

  return (
    <div>
      <div className="card" style={{ marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <div className="card-title" style={{ marginBottom: 0 }}>
              {loading ? 'Generating questions…' : `${questions.length} questions generated`}
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-500)', marginTop: 2 }}>
              {!loading && `Tick the questions you want answered. ${selected.length} selected.`}
            </div>
          </div>
          {!loading && questions.length > 0 && (
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm" onClick={loadQuestions}>↻ Regenerate</button>
              <button className="btn btn-sm" onClick={() => setSelected([...questions])}>All</button>
              <button className="btn btn-sm" onClick={() => setSelected([])}>None</button>
            </div>
          )}
        </div>

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--gray-500)', fontSize: 13, padding: '8px 0' }}>
            <span className="spinner" /> Generating questions for "{inputData.keyword}"…
          </div>
        )}
        {error && <div className="alert alert-error">{error}</div>}

        {!loading && questions.length > 0 && (
          <>
            <div style={{ display: 'grid', gap: 6, marginBottom: 12 }}>
              {questions.map((q, i) => (
                <label key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', padding: '8px 10px', borderRadius: 'var(--radius-md)', background: selected.includes(q) ? 'var(--blue-light)' : 'var(--gray-50)', border: `1px solid ${selected.includes(q) ? 'var(--blue-border)' : 'var(--gray-200)'}`, transition: 'all 0.1s' }}>
                  <input type="checkbox" checked={selected.includes(q)} onChange={() => toggle(q)} style={{ marginTop: 2, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: 'var(--gray-800)', lineHeight: 1.4 }}>{q}</span>
                </label>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input type="text" placeholder="Add your own question…" value={custom} onChange={e => setCustom(e.target.value)} onKeyDown={e => e.key === 'Enter' && addCustom()} style={{ flex: 1 }} />
              <button className="btn btn-sm" onClick={addCustom} disabled={!custom.trim()}>Add</button>
            </div>
          </>
        )}
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Answer length</div>
        <div style={{ display: 'flex', gap: 12 }}>
          {[{ val: 'short', label: 'Short', desc: '50–80 words — ideal for schema markup' }, { val: 'medium', label: 'Medium', desc: '100–150 words — ideal for page content' }].map(opt => (
            <label key={opt.val} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', flex: 1, padding: '10px 12px', borderRadius: 'var(--radius-md)', border: `1px solid ${answerLength === opt.val ? 'var(--blue-border)' : 'var(--gray-200)'}`, background: answerLength === opt.val ? 'var(--blue-light)' : 'var(--gray-50)' }}>
              <input type="radio" name="al" value={opt.val} checked={answerLength === opt.val} onChange={() => setAnswerLength(opt.val)} style={{ marginTop: 2 }} />
              <div><div style={{ fontSize: 13, fontWeight: 600 }}>{opt.label}</div><div style={{ fontSize: 12, color: 'var(--gray-500)' }}>{opt.desc}</div></div>
            </label>
          ))}
        </div>
      </div>

      <div className="card" style={{ marginBottom: 12 }}>
        <div className="card-title">Trust signals <span style={{ fontSize: 12, fontWeight: 400, color: 'var(--gray-500)' }}>optional</span></div>
        <div className="card-sub">Makes answers more credible and specific.</div>
        <div className="form-row form-row-2 form-group">
          <div><label>Phone number</label><input type="text" placeholder="e.g. 01737 773 768" value={eatInfo.phone} onChange={e => upEat('phone', e.target.value)} /></div>
          <div><label>Years trading</label><input type="text" placeholder="e.g. 15 years" value={eatInfo.years_trading} onChange={e => upEat('years_trading', e.target.value)} /></div>
        </div>
        <div className="form-row form-row-2 form-group">
          <div><label>Accreditations</label><input type="text" placeholder="e.g. Which? Trusted Trader" value={eatInfo.accreditations} onChange={e => upEat('accreditations', e.target.value)} /></div>
          <div><label>Key stats</label><input type="text" placeholder="e.g. 500+ 5-star reviews" value={eatInfo.stats} onChange={e => upEat('stats', e.target.value)} /></div>
        </div>
        <div className="form-group" style={{ marginBottom: 0 }}>
          <label>USPs</label>
          <input type="text" placeholder="e.g. Same-day service, eco-friendly, guaranteed results" value={eatInfo.unique_selling_points} onChange={e => upEat('unique_selling_points', e.target.value)} />
        </div>
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: 0 }}>
        <button className="btn" onClick={onBack}>← Back</button>
        <button className="btn btn-primary" onClick={() => onNext(selected, answerLength, eatInfo)} disabled={selected.length === 0 || loading}>
          Write {selected.length} FAQ answer{selected.length !== 1 ? 's' : ''} →
        </button>
      </div>
    </div>
  );
}

// ── Step 3: Results ────────────────────────────────────────────

function StepResults({ inputData, questions, answerLength, eatInfo, onBack, onRestart }) {
  const [faqs, setFaqs] = useState([]);
  const [writing, setWriting] = useState(true);
  const [progress, setProgress] = useState({ done: 0, total: questions.length });
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');

  useEffect(() => { generateFAQs(); }, []);

  const generateFAQs = async () => {
    setWriting(true);
    setFaqs([]);
    setProgress({ done: 0, total: questions.length });
    setError('');
    const all = [];
    const BATCH = 5;

    for (let i = 0; i < questions.length; i += BATCH) {
      const batch = questions.slice(i, i + BATCH);
      try {
        const res = await fetch(`${API_URL}/api/content/faqs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            questions: batch,
            keyword: inputData.keyword,
            service: inputData.service,
            location: inputData.location,
            businessName: inputData.businessName,
            businessDescription: inputData.description,
            eatInfo,
            answerLength,
          })
        });
        const data = await res.json();
        if (data.error) throw new Error(data.error);
        all.push(...(data.faqs || []));
        setFaqs([...all]);
      } catch (e) {
        console.error('FAQ batch error:', e);
        setError('One batch failed — partial results shown.');
      }
      setProgress({ done: Math.min(i + BATCH, questions.length), total: questions.length });
    }
    setWriting(false);
  };

  const copy = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    setTimeout(() => setCopied(''), 2000);
  };

  const downloadWord = async () => {
    const text = faqs.map((f, i) => `## Q${i + 1}: ${f.question}\n\n${f.answer}`).join('\n\n---\n\n');
    const res = await fetch(`${API_URL}/api/content/export-doc`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ content: text, metaTitle: `FAQs — ${inputData.keyword}`, metaDescription: '', keyword: `FAQs — ${inputData.keyword}`, businessName: inputData.businessName })
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url;
    a.download = `faqs-${inputData.keyword.replace(/\s+/g, '-').toLowerCase()}.docx`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div>
      {writing && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>Writing answers… {progress.done} of {progress.total}</div>
          <div style={{ background: 'var(--gray-100)', borderRadius: 4, height: 6, overflow: 'hidden' }}>
            <div style={{ background: 'var(--blue-mid)', height: '100%', width: `${(progress.done / progress.total) * 100}%`, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {error && <div className="alert alert-warning" style={{ marginBottom: 12 }}>{error}</div>}

      {faqs.length > 0 && !writing && (
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
            <div style={{ fontSize: 13, fontWeight: 500 }}>{faqs.length} FAQs for {inputData.businessName}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button className="btn btn-sm" onClick={() => copy(buildHTMLBlock(faqs), 'html')}>{copied === 'html' ? '✓ Copied!' : 'Copy HTML + Schema'}</button>
              <button className="btn btn-sm" onClick={() => copy(buildSchemaMarkup(faqs, inputData.businessName), 'schema')}>{copied === 'schema' ? '✓ Copied!' : 'Copy schema only'}</button>
              <button className="btn btn-sm" onClick={() => copy(faqs.map((f,i) => `Q${i+1}: ${f.question}\n\n${f.answer}`).join('\n\n---\n\n'), 'plain')}>{copied === 'plain' ? '✓ Copied!' : 'Copy plain text'}</button>
              <button className="btn btn-sm" onClick={downloadWord}>↓ Word doc</button>
              <button className="btn btn-sm" onClick={generateFAQs}>↻ Regenerate all</button>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: 10 }}>
        {faqs.map((faq, i) => (
          <div key={i} className="card" style={{ padding: '1rem 1.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, marginBottom: 10 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--blue)', lineHeight: 1.4, flex: 1 }}>{faq.question}</div>
              <div style={{ display: 'flex', gap: 6, flexShrink: 0, alignItems: 'center' }}>
                <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{(faq.answer || '').split(' ').length} words</span>
                <button className="btn btn-sm" style={{ fontSize: 11, padding: '3px 10px' }} onClick={() => navigator.clipboard.writeText(faq.question + '\n\n' + faq.answer)}>Copy</button>
              </div>
            </div>
            <textarea
              value={faq.answer || ''}
              onChange={e => setFaqs(prev => prev.map((f, j) => j === i ? { ...f, answer: e.target.value } : f))}
              rows={answerLength === 'short' ? 4 : 6}
              style={{ width: '100%', border: '1px solid var(--gray-200)', borderRadius: 'var(--radius-md)', padding: '8px 10px', fontSize: 13, lineHeight: 1.6, resize: 'vertical', fontFamily: 'inherit', color: 'var(--gray-800)', background: 'var(--gray-50)' }}
            />
          </div>
        ))}
      </div>

      <div className="actions" style={{ borderTop: 'none', paddingTop: '1rem', flexWrap: 'wrap', gap: 8 }}>
        <button className="btn" onClick={onBack}>← Back to questions</button>
        <button className="btn" onClick={onRestart}>Start over</button>
      </div>
    </div>
  );
}

// ── Main ───────────────────────────────────────────────────────

export default function FAQCreator({ onBack }) {
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [inputData, setInputData] = useState(null);
  const [savedClient, setSavedClient] = useState(null);
  const [selectedQs, setSelectedQs] = useState([]);
  const [answerLength, setAnswerLength] = useState('medium');
  const [eatInfo, setEatInfo] = useState({});

  useEffect(() => {
    supabase.from('clients')
      .select('id, name, url, description, location_code, phone, years_trading, accreditations, stats, unique_selling_points')
      .order('name')
      .then(({ data }) => { if (data) setClients(data); });
  }, []);

  const handleStep1 = (data) => {
    setInputData(data);
    setSavedClient(data.clientId ? (clients.find(c => c.id === data.clientId) || null) : null);
    setStep(2);
  };

  const handleStep2 = (qs, length, eat) => {
    setSelectedQs(qs); setAnswerLength(length); setEatInfo(eat); setStep(3);
  };

  const stepLabels = ['Service & client', 'Select questions', 'FAQ answers'];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
        <button className="btn btn-sm" onClick={onBack}>← Tools</button>
        <div>
          <div className="page-title">FAQ Creator</div>
          <div className="page-sub">Generate FAQ questions with AI then write SEO-optimised answers</div>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 0, marginBottom: '1.5rem', alignItems: 'center' }}>
        {stepLabels.map((label, i) => {
          const n = i + 1; const isDone = step > n; const isActive = step === n;
          return (
            <React.Fragment key={n}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 26, height: 26, borderRadius: '50%', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 600, cursor: isDone ? 'pointer' : 'default', background: isDone ? '#3B6D11' : isActive ? '#185FA5' : 'var(--gray-200)', color: isDone || isActive ? '#fff' : 'var(--gray-500)' }} onClick={() => isDone && setStep(n)}>
                  {isDone ? '✓' : n}
                </div>
                <span style={{ fontSize: 13, color: isActive ? 'var(--gray-900)' : 'var(--gray-500)', fontWeight: isActive ? 500 : 400 }}>{label}</span>
              </div>
              {i < stepLabels.length - 1 && <div style={{ flex: 1, height: 1, background: 'var(--gray-200)', margin: '0 12px' }} />}
            </React.Fragment>
          );
        })}
      </div>

      {step === 1 && <StepInput clients={clients} onNext={handleStep1} />}
      {step === 2 && <StepQuestions inputData={inputData} savedClient={savedClient} onBack={() => setStep(1)} onNext={handleStep2} />}
      {step === 3 && <StepResults inputData={inputData} questions={selectedQs} answerLength={answerLength} eatInfo={eatInfo} onBack={() => setStep(2)} onRestart={() => { setStep(1); setInputData(null); }} />}
    </div>
  );
}
